import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useSchools from './useSchools';
import useBrandSettings from './useBrandSettings';
import { readFreshSelection } from '../services/schoolSelectionTtl';
import { resolveShopDestination } from '../services/shopDestination';
import { shopEntry, schoolShopPath } from '../services/menuDestination';

export const STORAGE_KEYS = {
  clsdk: 'clsdk_school_selection',
  clsse: 'clsse_school_selection',
  abcse: 'abcse_school_selection',
  clsfi: 'clsfi_school_selection',
};

/**
 * Resolve a click on one of the brand's two fixed header menu items
 * ('menu_1' | 'menu_2') to the right destination:
 *
 *  1. Customer's school is known (fresh stored selection) and it has an open
 *     shop mapped to the menu item → that shop's school-shop URL (the
 *     SchoolShop page persists the selection + syncs the cart on mount).
 *  2. School known but no mapped shop → ANY open shop of the school (stored one
 *     first — since the Vogntrøjer→Studieturstrøjer merge a school has one shop
 *     selling both ranges); no open shop at all → the fallback listing page.
 *  3. No school + selector enabled → open the school popup carrying the menu
 *     intent (the header auto-picks the mapped shop after the school choice;
 *     dismissing just closes the popup — see the brand Header's listener).
 *  4. No school + selector disabled → straight to the fallback listing page.
 *
 * When the brand's open shop is DISABLED (`openShopEnabled: false`, the
 * "Disable Open Shop" admin flag) the fallback listing no longer exists as a
 * shopping destination — the /shop/{menuSlug} URL renders the localized
 * "shop is closed" page instead. The matrix changes to:
 *  2'. School known, no mapped shop: selector on → re-open the popup (the
 *      "login page": the customer picks a school that has this shop type);
 *      selector off → the closed page.
 *  3'. The dispatched intent carries `fallbackTo: null`, so the popup's
 *      post-select fallback goes to the school-shop root, never the open shop.
 *  4'. No school + selector disabled → the closed page.
 *
 * The school's shops come from the live schools API (self-heals stale
 * localStorage); the stored school.seasons are only used while that fetch is
 * still loading.
 *
 * LANDING clicks (`options.toSchoolShop`, optionally with `options.target` = the
 * clicked product / package card) follow services/shopDestination instead: the
 * mapped shop and, when it sells it, the clicked item's configurator → else the
 * shop page → school without that shop type: ANY open shop of the school →
 * school with no open shop: the closed page. Without a school the popup opens
 * carrying the same intent (the Header resolves it after the selection).
 *
 * OPEN SHOP: when the brand's open shop is configured for the menu item
 * (`menuItems[].openShop`), a school-less click that names no product goes to the item's
 * open shop page (/shop/{slug}) instead of the popup.
 *
 * @param {{brandSlug: string, feSlug: string}} args API slug + route slug.
 * @returns {(menuKey: string, options?: {toSchoolShop?: boolean, target?: object}) => void}
 */
export default function useMenuItemNavigation({ brandSlug, feSlug, anonToProductPage = false }) {
  const navigate = useNavigate();
  const { schools } = useSchools(brandSlug);
  const { showSchoolSelector, openShopEnabled, menuItems } = useBrandSettings(brandSlug);

  return useCallback((menuKey, options = null) => {
    const toSchoolShop = Boolean(options?.toSchoolShop);
    const target = toSchoolShop ? (options?.target ?? null) : null;

    // The public fallback URL uses the menu item's ASCII-folded label slug
    // (from the brand settings), not the internal menu_1/menu_2 key. If the
    // slug isn't loaded yet, fall back to the brand landing.
    const menuSlug = (menuItems || []).find((m) => m.key === menuKey)?.slug;
    const fallbackTo = menuSlug ? `/clothing/${feSlug}/shop/${menuSlug}` : `/clothing/${feSlug}`;

    const stored = readFreshSelection(STORAGE_KEYS[brandSlug]);
    const storedSchool = stored?.school ?? null;

    if (storedSchool?.slug || storedSchool?.id) {
      // Prefer the live schools list (fresh menuItemKey data); fall back to
      // the persisted school object while the list is still loading.
      const liveSchool = schools.find(
        (s) => (storedSchool.slug && s.slug === storedSchool.slug)
            || String(s.id) === String(storedSchool.id),
      );
      const school = liveSchool ?? storedSchool;
      const schoolSlug = school?.slug ?? storedSchool.slug;
      // Entry rule (services/menuDestination): the shop whose group / that is
      // mapped to the menu item, else the stored / first open shop; inside it
      // the mapped group, else the first group (root only for group-less shops).
      const { shop: entryShop, group: entryGroup } = shopEntry({ ...school, slug: schoolSlug }, menuKey, stored?.season ?? null);

      if (toSchoolShop) {
        resolveShopDestination({
          brandSlug, feSlug, school: { ...school, slug: schoolSlug }, menuKey,
          storedShop: stored?.season ?? null, className: stored?.class ?? null,
          target, closedPath: fallbackTo,
        }).then((path) => navigate(path));
        return;
      }

      if (entryShop?.slug && schoolSlug) {
        navigate(schoolShopPath(feSlug, schoolSlug, entryShop, entryGroup));
      } else if (openShopEnabled) {
        navigate(fallbackTo);
      } else if (showSchoolSelector) {
        // Open shop disabled + this shop type isn't open for the customer's
        // school → back to the "login page" (the selector popup) to pick a
        // school that has it. `fallbackTo: null` keeps the post-select
        // fallback off the open shop (Header navigates to school-shop root).
        document.dispatchEvent(new CustomEvent('open-school-selector', {
          detail: { menuItemKey: menuKey, fallbackTo: null },
        }));
      } else {
        // No popup on this brand — the /shop URL renders the closed page.
        navigate(fallbackTo);
      }
      return;
    }

    // CLS-DK (`anonToProductPage`): a visitor WITHOUT a school who clicks a product /
    // package card goes straight to that product's page — it shows the gallery and a
    // school + class picker instead of the configurator (no popup first). Cards whose
    // target has no slug (static demo product cards) still open the popup.
    if (anonToProductPage && target?.slug) {
      navigate(target.type === 'package'
        ? `/clothing/${feSlug}/product-package/${encodeURIComponent(target.slug)}`
        : `/clothing/${feSlug}/product-details/${encodeURIComponent(target.slug)}`
          + (target.id != null ? `?variation=${encodeURIComponent(target.id)}` : ''));
      return;
    }

    // OPEN SHOP configured for this menu item (brand payload `menuItems[].openShop`:
    // enabled + a group with collections is linked to it): a visitor without a school
    // goes to the item's public open shop page — no popup. Covers the header and footer
    // links, the landing hero buttons and the "see more" CTAs (they all come through here).
    if (menuSlug && (menuItems || []).find((m) => m.key === menuKey)?.openShop) {
      navigate(fallbackTo);
      return;
    }

    if (showSchoolSelector) {
      document.dispatchEvent(new CustomEvent('open-school-selector', {
        detail: {
          menuItemKey: menuKey,
          fallbackTo: openShopEnabled ? fallbackTo : null,
          ...(toSchoolShop ? { toSchoolShop: true, target, closedPath: fallbackTo } : {}),
        },
      }));
    } else {
      // Selector disabled for the brand — its listener is gated off, so a
      // dispatched event would be silently swallowed. Go straight to the
      // fallback listing (or, with the open shop disabled, the closed page
      // that same URL now renders).
      navigate(fallbackTo);
    }
  }, [navigate, schools, showSchoolSelector, openShopEnabled, menuItems, brandSlug, feSlug, anonToProductPage]);
}
